import type { CustomerJobMetadataPolicy } from '~/lib/customer-job-metadata';

/**
 * BRE: which fields of each service a customer's post may set (lib/customer-
 * job-metadata.ts), by service id. Every field of every service's schema is
 * listed once, customer or office (customer-job-metadata.test.ts).
 */
export const BRE_CUSTOMER_JOB_METADATA: Record<string, CustomerJobMetadataPolicy> = {
  website: { customer: ['type', 'domain'], office: [] },
  // BrePhotosForm shows the office alone the editing credits and extra photos; AI editing is chosen on
  // submit.
  photos: {
    customer: ['photos', 'max_extra_photos', 'extras_consent', 'notes', 'twilight', 'local_area', 'community', 'rush'],
    office: ['ai_edit', 'extra_photos', 'editing_credits'],
  },
  // BreTwilightForm: editing credits are the office's.
  twilight: { customer: ['photos', 'shoot_date', 'notes', 'copyright'], office: ['editing_credits'] },
  floorplan: { customer: ['interactive_tour', 'gla_report', 'cad_file', 'floorplan_3d', 'rush'], office: [] },
  // no customer form: the scan's own square footage (BreMatterportJob prices from it) and the 2D
  // floorplan, which only the office's form offers (as `foorplan`).
  matterport: { customer: ['outdoor', 'outdoor_commercial', 'site_plan', 'plan'], office: ['sqft', 'floorplan'] },
  zillow: { customer: [], office: [] },
  // BreAerialForm/Submit: self-edited, AI editing and editing credits are the office's and the
  // photographer's.
  aerial: { customer: ['type', 'poi'], office: ['self_edited', 'ai_edit', 'editing_credits'] },
  fpv: { customer: ['indoor'], office: [] },
  // BreSocialForm: self-edited is the office's.
  social: { customer: ['intro', 'narration', 'voiceover'], office: ['self_edited'] },
  // BreCinematicForm: self-edited and editing credits are the office's.
  cinematic: {
    customer: ['aerial_upgrade', 'intro', 'narration', 'kwasi', 'twilight', 'aerial_photos', 'duration'],
    office: ['editing_credits', 'self_edited'],
  },
  staging: { customer: ['style', 'quantity', 'item_removal', 'space_renovation'], office: [] },
  dusk: { customer: ['quantity'], office: [] },
  community: { customer: ['photos'], office: [] },
  flyers: { customer: ['sides', 'count', 'paper', 'need_by', 'no_free_uv', 'content_writing', 'design'], office: [] },
  brochures: {
    customer: ['sides', 'count', 'paper', 'need_by', 'no_free_uv', 'content_writing', 'design'],
    office: [],
  },
  booklets: { customer: ['sides', 'count', 'paper', 'need_by', 'no_free_uv', 'content_writing'], office: [] },
  design: { customer: ['layout', 'type'], office: [] },
  riders: { customer: ['holes', 'shipping', 'need_by', 'no_qr_code'], office: [] },
  boost: { customer: ['reach', 'start_at'], office: [] },
  'postcards-mailing': {
    customer: ['count', 'size', 'paper', 'postage', 'announcement_type', 'need_by', 'design'],
    office: [],
  },
  'postcards-printonly': { customer: ['count', 'need_by', 'design'], office: [] },
  slideshow: { customer: [], office: [] },
  guides: { customer: ['types', 'need_by'], office: [] },
  portraits: { customer: ['type', 'extra_edit', 'extra_pose'], office: [] },
  'business-cards': { customer: ['count', 'paper', 'need_by'], office: [] },
  letterheads: { customer: ['count', 'paper', 'need_by'], office: [] },
  envelopes: { customer: ['count', 'paper', 'need_by'], office: [] },
  folders: { customer: ['count', 'need_by'], office: [] },
  covers: { customer: ['count', 'need_by'], office: [] },
  signs: { customer: ['type', 'shipping', 'need_by'], office: [] },
  // the office's own service (hidden from every customer): every field is the office's.
  custom: { customer: [], office: ['description', 'rate', 'pay', 'onsite', 'taxable'] },
};
